/**
 * ChatInner.tsx
 *
 * Main chat inner container component.
 * Refactored from ChatInner.v2.tsx (~1447 lines) to use extracted components and hooks.
 */

import { FC, useEffect, useRef, useState, useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

// Redux
import { RootState } from "src/setup/redux/store";
import { fetchOlderMessagesForRoom } from "src/app/modules/chat/redux/ChatThunk";

// Components
import ChatMessageList from "./ChatMessageList";
import ChatInputArea from "./ChatInputArea";
import { MediaModalProvider } from "../../context/MediaModalContext";
import { MediaModal } from "../media-modal/MediaModal";

// Hooks
import { useFileUpload } from "../../hooks/useFileUpload";
import { useMessageSender } from "../../hooks/useMessageSender";

// Styles
import "src/styles/css/chat.scss";
import "src/styles/css/dropdown.scss";
import "src/styles/css/margin.scss";

const ChatInner: FC = () => {
  const dispatch = useDispatch();

  // Refs
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const hasInitiallyScrolledRef = useRef<boolean>(false);
  const isUserAtBottomRef = useRef<boolean>(true);
  const isLoadingOlderMessagesRef = useRef<boolean>(false);
  const previousScrollHeightRef = useRef<number>(0);
  const previousMessageCountRef = useRef<number>(0);
  const previousRoomIdRef = useRef<string | null>(null);

  // Redux selectors
  const { replyToMessage, orderInitiateMessage, chatInput } = useSelector(
    (state: RootState) => state.Chat
  );
  const { activeTab } = useSelector(
    (state: RootState) => state.ChatV2,
    shallowEqual
  );

  const { collaborationsById, selectedCollaborationId, selectedRoomId } =
    useSelector(
      (state: RootState) =>
        state.ChatV2[activeTab] ?? {
          collaborationOrder: [],
          collaborationsById: {},
          hasMore: false,
          isLoading: false,
          selectedCollaborationId: null,
          selectedRoomId: null,
        },
      shallowEqual
    );

  const messages = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.messages ?? [],
    shallowEqual
  );

  const lastVisible = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.lastVisible ?? null
  );

  const hasMore = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.hasMore ?? true
  );

  const isLoading = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.isLoading ?? false
  );

  const selectedCollaboration = collaborationsById[selectedCollaborationId];
  const selectedRoom =
    collaborationsById[selectedCollaborationId]?.roomsModel?.find(
      (item) => item?.id === selectedRoomId
    ) ?? collaborationsById[selectedCollaborationId]?.roomsModel?.[0];

  // Local state
  const [message, setMessage] = useState<string>("");
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [debug, setDebug] = useState<boolean>(false);

  // Custom hooks for file upload and message sending
  const {
    file,
    fileSize,
    inputFileName,
    messageType,
    setFile,
    setFileSize,
    setInputFileName,
    setMessageType,
    uploadFile,
    uploadMediaFallback,
    handleFileSelect,
    removeFile,
  } = useFileUpload({
    selectedRoom,
    selectedCollaboration,
  });

  const { isSending, sendMessage: sendMessageHook } = useMessageSender({
    selectedCollaboration,
    selectedRoom,
    replyToMessage,
    orderInitiateMessage,
    file,
    inputFileName,
    fileSize,
    messageType,
    uploadFile,
    uploadMediaFallback,
  });

  // Wrapper functions for hook methods
  const clearMessageState = useCallback(() => {
    setMessage("");
    setFile(undefined);
    setFileSize("");
    setInputFileName("");
    setMessageType("text");
  }, [setFile, setFileSize, setInputFileName, setMessageType]);

  const sendMessage = useCallback(
    async (templateLanguage?: any) => {
      await sendMessageHook(message, clearMessageState, templateLanguage);
    },
    [sendMessageHook, message, clearMessageState]
  );

  const onEnterPress = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.keyCode === 13 && !e.shiftKey && message.trim() !== "") {
        e.preventDefault();
        sendMessage();
      }
    },
    [message, sendMessage]
  );

  const handleRemoveFile = useCallback(() => {
    setMessage("");
    removeFile();
  }, [removeFile]);

  // Scroll handler
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

      const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;
      isUserAtBottomRef.current = isAtBottom;

      if (
        hasMore &&
        scrollTop < 3 &&
        !isLoadingOlderMessagesRef.current &&
        selectedRoom?.conversationId
      ) {
        isLoadingOlderMessagesRef.current = true;

        dispatch(
          fetchOlderMessagesForRoom({
            collaborationId: selectedCollaborationId,
            roomId: selectedRoomId,
            conversationId: selectedRoom?.conversationId,
            lastVisible,
          })
        );
      }

      if (scrollHeight) {
        setShowScrollBottom(scrollHeight - scrollTop >= 800);
      }
    },
    [
      hasMore,
      messages.length,
      selectedCollaborationId,
      selectedRoomId,
      selectedRoom?.conversationId,
      lastVisible,
      dispatch,
    ]
  );

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    const chatBody = chatBodyRef.current;
    if (chatBody) {
      chatBody.scrollTop = chatBody.scrollHeight - chatBody.clientHeight;
    }
  }, []);

  const smoothScrollToBottom = useCallback(() => {
    const chatBody = chatBodyRef.current;
    if (chatBody) {
      chatBody.scrollTo({
        top: chatBody.scrollHeight - chatBody.clientHeight,
        behavior: "smooth",
      });
      setShowScrollBottom(false);
    }
  }, []);

  // Debug shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const isCmd = isMac ? event.metaKey : event.ctrlKey;

      if (isCmd && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setDebug((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Scroll effects
  useEffect(() => {
    const roomChanged = previousRoomIdRef.current !== selectedRoomId;

    if (messages.length > 0 && chatBodyRef.current) {
      if (roomChanged || !hasInitiallyScrolledRef.current) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToBottom();
            hasInitiallyScrolledRef.current = true;
            previousRoomIdRef.current = selectedRoomId;
          });
        });
      }
    }
  }, [messages.length, selectedRoomId, scrollToBottom]);

  useEffect(() => {
    if (previousRoomIdRef.current !== selectedRoomId) {
      hasInitiallyScrolledRef.current = false;
      isUserAtBottomRef.current = true;
      isLoadingOlderMessagesRef.current = false;
      previousMessageCountRef.current = 0;
    }
  }, [selectedRoomId]);

  // Reset loading flag when Redux isLoading changes to false
  useEffect(() => {
    if (!isLoading) {
      isLoadingOlderMessagesRef.current = false;
    }
  }, [isLoading]);

  return (
    <MediaModalProvider>
      <div
        className="d-flex w-100 h-100 flex-column m-0 p-0"
        style={{
          minHeight: 0, // Critical for flex scroll children
          overflow: "hidden",
        }}
        id="kt_chat_messenger_body"
      >
        {/* Message list */}
        <ChatMessageList
          messages={messages}
          collaboration={selectedCollaboration}
          isLoading={isLoading}
          hasMore={hasMore}
          debug={debug}
          onScroll={handleScroll}
          showScrollBottom={showScrollBottom}
          onScrollToBottom={smoothScrollToBottom}
        />

        {/* Input area */}
        <ChatInputArea
          message={message}
          file={file}
          inputFileName={inputFileName}
          messageType={messageType}
          isSending={isSending}
          replyToMessage={replyToMessage}
          orderInitiateMessage={orderInitiateMessage}
          collaboration={selectedCollaboration}
          companyId={String(selectedCollaboration?.company?.id)}
          channel={selectedRoom?.channel}
          onMessageChange={setMessage}
          onFileSelect={handleFileSelect}
          onRemoveFile={handleRemoveFile}
          onSendMessage={sendMessage}
          onEnterPress={onEnterPress}
        />

        {/* Single shared modal for all media in chat */}
        <MediaModal />
      </div>
    </MediaModalProvider>
  );
};

export default ChatInner;
